import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Input, InputNumber, Popconfirm, Select, Spin } from "antd";
import toast from "react-hot-toast";
import { useNavigate, useParams } from "react-router-dom";
import LoadableButton from "../../components/buttons/LoadableButton.jsx";
import useModulePermissions from "../../hooks/useModulePermissions.jsx";
import {
  GetIBCommissionOverridesApi,
  SaveIBCommissionOverridesApi,
} from "../../api/request/ibapproved.js";
import "./CommissionOverrides.scss";

const SCOPE_GROUP = 1;
const SCOPE_SYMBOL = 2;

const isGroupScope = (scope) =>
  scope === SCOPE_GROUP || scope === "Group" || scope === "group";
const isSymbolScope = (scope) =>
  scope === SCOPE_SYMBOL || scope === "Symbol" || scope === "symbol";

const money = (value) =>
  `$${(Number.isFinite(Number(value)) ? Number(value) : 0).toFixed(2)}`;

const snapshotOf = (rows) =>
  JSON.stringify(
    (rows || []).map((row) => ({
      scope: isGroupScope(row.scope) ? SCOPE_GROUP : SCOPE_SYMBOL,
      symbolCrmGroupId: row.symbolCrmGroupId || null,
      symbolName: row.symbolName || null,
      allocations: (row.allocations || []).map((share) => ({
        ibUserId: share.ibUserId,
        commissionPerLot: Number(share.commissionPerLot || 0),
      })),
    })),
  );

const sameId = (left, right) =>
  String(left || "").toLowerCase() === String(right || "").toLowerCase();

const rowTotal = (row) =>
  (row.allocations || []).reduce(
    (sum, share) => sum + Number(share.commissionPerLot || 0),
    0,
  );

const allocationsFromHierarchy = (hierarchy, existing, fallbackTotal) => {
  const columns = hierarchy || [];
  const current = existing || [];
  if (current.length > 0) {
    return columns.map((column) => ({
      ibUserId: column.ibUserId,
      commissionPerLot:
        current.find((share) => sameId(share.ibUserId, column.ibUserId))
          ?.commissionPerLot ?? 0,
    }));
  }

  const defaultTotal = columns.reduce(
    (sum, column) => sum + Number(column.defaultCommission || 0),
    0,
  );
  const factor =
    defaultTotal > 0 && fallbackTotal != null ? Number(fallbackTotal) / defaultTotal : 1;

  return columns.map((column) => ({
    ibUserId: column.ibUserId,
    commissionPerLot: Number(
      (Number(column.defaultCommission || 0) * factor).toFixed(2),
    ),
  }));
};

export default function CommissionOverrides() {
  const { id, type: approveType, planId } = useParams();
  const navigate = useNavigate();
  const permissions = useModulePermissions("ib_manager", "plan");
  const canUpdate = !!permissions.update;
  const isTemp = approveType === "pending";

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [page, setPage] = useState(null);
  const [overrides, setOverrides] = useState([]);
  const [savedSnapshot, setSavedSnapshot] = useState("[]");
  const [expandedGroups, setExpandedGroups] = useState({});
  const [groupSymbolSearch, setGroupSymbolSearch] = useState({});

  const load = useCallback(async () => {
    if (!id || !planId || planId === "0") {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const { data } = await GetIBCommissionOverridesApi(id, planId, isTemp);
      if (data?.success) {
        const hierarchy = data.data?.hierarchy || [];
        const rows = (data.data?.overrides || []).map((row) => ({
          ...row,
          allocations: allocationsFromHierarchy(
            hierarchy,
            row.allocations,
            row.commissionPerLot,
          ),
        }));
        setPage(data.data);
        setOverrides(rows);
        setSavedSnapshot(snapshotOf(rows));
        const open = {};
        rows.forEach((row) => {
          if (isGroupScope(row.scope) && row.symbolCrmGroupId) {
            open[row.symbolCrmGroupId] = true;
          }
        });
        setExpandedGroups(open);
      } else {
        toast.error(data?.message || "Failed to load overrides.");
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.error?.Message ||
          error?.response?.data?.error?.message ||
          error?.response?.data?.message ||
          "Failed to load overrides.",
      );
    } finally {
      setLoading(false);
    }
  }, [id, planId, isTemp]);

  useEffect(() => {
    load();
  }, [load]);

  const isDirty = snapshotOf(overrides) !== savedSnapshot;
  const maxTotal = Number(page?.totalPerLot || 0);

  const rowErrors = useMemo(() => {
    return overrides.map((row) => {
      const errors = { fields: {}, messages: [] };
      const total = rowTotal(row);
      if (maxTotal > 0 && total > maxTotal + 0.0001) {
        errors.fields.total = true;
        errors.messages.push(
          `Total ${money(total)} cannot exceed the assigned IB distribution ${money(maxTotal)}`,
        );
      }
      return errors;
    });
  }, [overrides, maxTotal]);

  const hasRowErrors = rowErrors.some((errors) => errors.messages.length > 0);

  const usedGroupIds = useMemo(
    () =>
      new Set(
        overrides
          .filter((row) => isGroupScope(row.scope) && row.symbolCrmGroupId)
          .map((row) => String(row.symbolCrmGroupId).toLowerCase()),
      ),
    [overrides],
  );

  const usedSymbols = useMemo(
    () =>
      new Set(
        overrides
          .filter((row) => isSymbolScope(row.scope) && row.symbolName)
          .map((row) => row.symbolName.toLowerCase()),
      ),
    [overrides],
  );

  const groupMembers = useCallback(
    (groupId) =>
      (page?.catalog?.symbols || [])
        .filter((symbol) => String(symbol.groupId) === String(groupId))
        .map((symbol) => ({
          symbolName: symbol.symbolName,
          groupName: symbol.groupName,
          hasOwnOverride: usedSymbols.has((symbol.symbolName || "").toLowerCase()),
        })),
    [page, usedSymbols],
  );

  const groupOptions = useMemo(
    () =>
      (page?.catalog?.groups || [])
        .filter((group) => !usedGroupIds.has(String(group.id).toLowerCase()))
        .map((group) => ({
          value: `g:${group.id}`,
          label: group.name,
          search: `${group.name} ${group.symbolCount} symbols`,
          extra: `${group.symbolCount} symbols`,
        })),
    [page, usedGroupIds],
  );

  const symbolOptions = useMemo(
    () =>
      (page?.catalog?.symbols || [])
        .filter((symbol) => !usedSymbols.has((symbol.symbolName || "").toLowerCase()))
        .map((symbol) => ({
          value: `s:${symbol.symbolName}`,
          label: symbol.symbolName,
          search: `${symbol.symbolName} ${symbol.description || ""} ${symbol.groupName || ""}`,
          extra: symbol.groupName || "Ungrouped",
          description: symbol.description,
        })),
    [page, usedSymbols],
  );

  const addSymbolOverride = (symbolName) => {
    if (!page || !symbolName) return;
    const symbol = (page.catalog?.symbols || []).find(
      (item) => (item.symbolName || "").toLowerCase() === symbolName.toLowerCase(),
    );
    if (!symbol) return;

    setOverrides((prev) => {
      if (
        prev.some(
          (row) =>
            isSymbolScope(row.scope) &&
            (row.symbolName || "").toLowerCase() === symbolName.toLowerCase(),
        )
      ) {
        return prev;
      }

      const groupRow = prev.find(
        (row) =>
          isGroupScope(row.scope) &&
          symbol.groupId &&
          String(row.symbolCrmGroupId) === String(symbol.groupId),
      );

      return [
        ...prev,
        {
          scope: SCOPE_SYMBOL,
          symbolName: symbol.symbolName,
          symbolCrmGroupId: symbol.groupId,
          groupName: symbol.groupName,
          allocations: allocationsFromHierarchy(
            page.hierarchy,
            groupRow?.allocations,
            groupRow?.commissionPerLot ?? page.totalPerLot,
          ),
        },
      ];
    });
  };

  const handleAdd = (value) => {
    if (!value || !page) return;
    if (value.startsWith("g:")) {
      const groupId = value.slice(2);
      const group = (page.catalog?.groups || []).find(
        (item) => String(item.id) === String(groupId),
      );
      if (!group) return;
      setOverrides((prev) => [
        ...prev,
        {
          scope: SCOPE_GROUP,
          symbolCrmGroupId: group.id,
          groupName: group.name,
          allocations: allocationsFromHierarchy(page.hierarchy, null, page.totalPerLot),
        },
      ]);
      setExpandedGroups((prev) => ({ ...prev, [group.id]: true }));
      return;
    }
    addSymbolOverride(value.slice(2));
  };

  const handleAmountChange = (index, ibUserId, value) => {
    const next = Math.max(Number(value || 0), 0);
    setOverrides((prev) =>
      prev.map((row, rowIndex) => {
        if (rowIndex !== index) return row;
        const allocations = (row.allocations || []).map((share) =>
          sameId(share.ibUserId, ibUserId)
            ? { ...share, commissionPerLot: next }
            : share,
        );
        return { ...row, allocations };
      }),
    );
  };

  const handleRemove = (index) => {
    setOverrides((prev) => prev.filter((_, rowIndex) => rowIndex !== index));
  };

  const toggleGroup = (groupId) => {
    setExpandedGroups((prev) => ({ ...prev, [groupId]: !prev[groupId] }));
  };

  const setGroupSearch = (groupId, value) => {
    setGroupSymbolSearch((prev) => ({ ...prev, [groupId]: value }));
  };

  const handleSave = async () => {
    if (!canUpdate) return;
    if (hasRowErrors) {
      toast.error(
        `Each override total must be ${money(maxTotal)} or less.`,
      );
      return;
    }
    setSaving(true);
    try {
      const { data } = await SaveIBCommissionOverridesApi({
        ibUserId: id,
        planId,
        isTemp,
        items: overrides.map((row) => ({
          scope: isGroupScope(row.scope) ? SCOPE_GROUP : SCOPE_SYMBOL,
          symbolCrmGroupId: row.symbolCrmGroupId || null,
          symbolName: row.symbolName || null,
          commissionPerLot: rowTotal(row),
          allocations: (row.allocations || []).map((share) => ({
            ibUserId: share.ibUserId,
            commissionPerLot: share.commissionPerLot || 0,
          })),
        })),
      });
      if (data?.success) {
        toast.success(data.message);
        await load();
      } else {
        toast.error(data?.message || "Failed to save overrides.");
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.error?.Message ||
          error?.response?.data?.error?.message ||
          error?.response?.data?.message ||
          "Failed to save overrides.",
      );
    } finally {
      setSaving(false);
    }
  };

  const goBack = () => navigate(`/ib_config/${approveType}/${id}/${planId || 0}`);

  const filterSearch = (input, option) => {
    const haystack = [
      option?.search,
      option?.data?.search,
      option?.label,
      option?.data?.label,
      option?.value,
      option?.data?.value,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
    return haystack.includes(input.trim().toLowerCase());
  };

  if (!planId || planId === "0") {
    return (
      <div className="commission-overrides-page">
        <button type="button" className="overrides-back" onClick={goBack}>
          ← Back to IB Config
        </button>
        <div className="overrides-empty-page">
          <p>Select an account type on IB Config first, then open Overrides.</p>
        </div>
      </div>
    );
  }

  if (loading && !page) {
    return (
      <div className="commission-overrides-page">
        <div className="overrides-loader">
          <Spin size="large" />
          <p>Loading commission overrides...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="commission-overrides-page">
      <div className="overrides-top">
        <button type="button" className="overrides-back" onClick={goBack}>
          ← Back to IB Config
        </button>
        <div className="overrides-heading">
          <div>
            <h1 className="overrides-title">Commission overrides</h1>
            <p className="overrides-subtitle">
              Change commission for selected symbol groups or single symbols.
              Everything else keeps the default plan split below.
            </p>
          </div>
          <div className="overrides-ib-meta">
            <span className="overrides-ib-name">{page?.ibName || "IB"}</span>
            {page?.ibEmail ? (
              <span className="overrides-ib-email">{page.ibEmail}</span>
            ) : null}
            <span className="overrides-plan-chip">
              {page?.planName || "Account type"}
            </span>
          </div>
        </div>
      </div>

      <div className="overrides-guide">
        <div className="overrides-guide-card">
          <span>1</span>
          <div>
            <p>Default plan</p>
            <small>
              All symbols pay the Mix split already assigned on IB Config.
            </small>
          </div>
        </div>
        <div className="overrides-guide-card">
          <span>2</span>
          <div>
            <p>Override a group</p>
            <small>
              One row applies the new amounts to every symbol in that group.
            </small>
          </div>
        </div>
        <div className="overrides-guide-card">
          <span>3</span>
          <div>
            <p>Override one symbol</p>
            <small>
              Use this when one pair needs a different split. Symbol wins over
              its group.
            </small>
          </div>
        </div>
      </div>

      <div className="overrides-add-grid">
        <div className="overrides-add-card">
          <div className="overrides-add-head">
            <span className="overrides-badge is-group">Group</span>
            <h2>Add a symbol group</h2>
          </div>
          <p>
            Start here if several symbols should share the same override, for
            example Majors or Metals.
          </p>
          <Select
            showSearch
            allowClear
            className="overrides-add"
            placeholder="Search group name, e.g. Majors"
            value={null}
            disabled={!canUpdate || loading}
            options={groupOptions}
            optionFilterProp="search"
            listHeight={320}
            popupClassName="overrides-select-dropdown"
            filterOption={filterSearch}
            optionRender={(option) => (
              <div className="overrides-option">
                <strong>{option.data.label}</strong>
                <span>{option.data.extra}</span>
              </div>
            )}
            onSelect={handleAdd}
          />
          <small>{groupOptions.length} groups available to add</small>
        </div>

        <div className="overrides-add-card">
          <div className="overrides-add-head">
            <span className="overrides-badge is-symbol">Symbol</span>
            <h2>Add a single symbol</h2>
          </div>
          <p>
            Use this for one instrument only, such as EURUSD or USDCAD. Type
            the symbol name to find it.
          </p>
          <Select
            showSearch
            allowClear
            className="overrides-add"
            placeholder="Search symbol, e.g. EURUSD"
            value={null}
            disabled={!canUpdate || loading}
            options={symbolOptions}
            optionFilterProp="search"
            listHeight={320}
            popupClassName="overrides-select-dropdown"
            filterOption={filterSearch}
            optionRender={(option) => (
              <div className="overrides-option">
                <strong>{option.data.label}</strong>
                {option.data.extra ? <span>{option.data.extra}</span> : null}
                {option.data.description ? (
                  <small>{option.data.description}</small>
                ) : null}
              </div>
            )}
            onSelect={handleAdd}
          />
          <small>{symbolOptions.length} symbols available to add</small>
        </div>
      </div>

      <div className="overrides-card">
        <div className="overrides-plan-row">
          <div>
            <p className="overrides-plan-name">Commission per IB</p>
            <p className="overrides-hint">
              Split amounts across IBs as needed. The row total must stay at or
              below the assigned IB distribution ({money(maxTotal)}).
            </p>
          </div>
          {isDirty && <span className="overrides-dirty">Unsaved changes</span>}
        </div>

        <Spin spinning={loading} tip="Refreshing overrides...">
          <div
            className="overrides-table"
            style={{ "--ib-cols": Math.max((page?.hierarchy || []).length, 1) }}
          >
            <div className="overrides-head">
              <span>Applied to</span>
              {(page?.hierarchy || []).map((column) => (
                <span key={column.ibUserId}>
                  <span className="overrides-col-title">
                    {column.ibName || "IB"}
                  </span>
                  <span className="overrides-col-role">{column.role}</span>
                </span>
              ))}
              <span>
                Total per lot
                <span className="overrides-col-cap">max {money(maxTotal)}</span>
              </span>
              <span />
            </div>

            <div className="overrides-row is-default">
              <p className="overrides-label">
                <span className="overrides-badge is-default">Default</span>
                <span className="overrides-label-text">All other symbols</span>
              </p>
              {(page?.hierarchy || []).map((column) => (
                <span className="overrides-static" key={column.ibUserId}>
                  {money(column.defaultCommission)}
                </span>
              ))}
              <span className="overrides-total">{money(page?.totalPerLot)}</span>
              <span />
            </div>

            {overrides.length === 0 && (
              <div className="overrides-empty">
                No overrides yet. Add a group or a symbol above, then set each
                IB&apos;s amount in this table.
              </div>
            )}

            {overrides.map((row, index) => {
              const group = isGroupScope(row.scope);
              const groupId = row.symbolCrmGroupId;
              const expanded = !!(groupId && expandedGroups[groupId]);
              const members = group ? groupMembers(groupId) : [];
              const symbolQuery = (
                (groupId && groupSymbolSearch[groupId]) ||
                ""
              ).trim().toLowerCase();
              const visibleMembers = symbolQuery
                ? members.filter((member) =>
                    `${member.symbolName} ${member.groupName || ""}`
                      .toLowerCase()
                      .includes(symbolQuery),
                  )
                : members;
              const errors = rowErrors[index] || { fields: {}, messages: [] };
              const invalid = errors.messages.length > 0;

              return (
                <React.Fragment
                  key={`${row.scope}-${groupId || row.symbolName}-${index}`}
                >
                  <div
                    className={`overrides-row ${group ? "is-group" : "is-symbol"} ${
                      invalid ? "is-invalid" : ""
                    }`}
                    onClick={group ? () => toggleGroup(groupId) : undefined}
                  >
                    <p className="overrides-label">
                      {group ? (
                        <>
                          <span className="overrides-expand">
                            {expanded ? "▾" : "▸"}
                          </span>
                          <span className="overrides-badge is-group">Group</span>
                          <span className="overrides-label-text">
                            {row.groupName || "Group"}
                          </span>
                          <span className="overrides-count">
                            {members.length} symbols
                          </span>
                        </>
                      ) : (
                        <>
                          <span className="overrides-badge is-symbol">
                            Added symbol
                          </span>
                          <span className="overrides-tag is-symbol-name">
                            {row.symbolName}
                          </span>
                          {row.groupName ? (
                            <span className="overrides-badge is-group">
                              Overrides {row.groupName}
                            </span>
                          ) : (
                            <span className="overrides-badge is-default">
                              Ungrouped
                            </span>
                          )}
                        </>
                      )}
                    </p>
                    {(page?.hierarchy || []).map((column) => {
                      const share = (row.allocations || []).find((item) =>
                        sameId(item.ibUserId, column.ibUserId),
                      );
                      const over =
                        !!errors.fields[String(column.ibUserId).toLowerCase()];
                      return canUpdate ? (
                        <div key={column.ibUserId} className="overrides-amount-wrap">
                          <InputNumber
                            min={0}
                            step={0.01}
                            precision={2}
                            value={share?.commissionPerLot || 0}
                            className="overrides-amount"
                            prefix="$"
                            controls={false}
                            status={over ? "error" : ""}
                            onClick={(event) => event.stopPropagation()}
                            onChange={(value) =>
                              handleAmountChange(index, column.ibUserId, value)
                            }
                          />
                        </div>
                      ) : (
                        <span className="overrides-static" key={column.ibUserId}>
                          {money(share?.commissionPerLot)}
                        </span>
                      );
                    })}
                    <span
                      className={`overrides-total ${
                        errors.fields.total ? "is-error" : ""
                      }`}
                    >
                      {money(rowTotal(row))}
                    </span>
                    <span onClick={(event) => event.stopPropagation()}>
                      {canUpdate && (
                        <Popconfirm
                          title="Remove this override?"
                          okText="Remove"
                          cancelText="Keep"
                          onConfirm={() => handleRemove(index)}
                        >
                          <button
                            type="button"
                            className="overrides-remove"
                            aria-label="Remove"
                          >
                            ×
                          </button>
                        </Popconfirm>
                      )}
                    </span>
                  </div>
                  {invalid && (
                    <div className="overrides-row-error">
                      {errors.messages.join(". ")}.
                    </div>
                  )}

                  {group && expanded && (
                    <div
                      className="overrides-group-search"
                      onClick={(event) => event.stopPropagation()}
                    >
                      <Input
                        allowClear
                        size="middle"
                        placeholder="Search symbols in this group, e.g. EURUSD"
                        value={groupSymbolSearch[groupId] || ""}
                        onChange={(event) =>
                          setGroupSearch(groupId, event.target.value)
                        }
                      />
                      <span>
                        {visibleMembers.length === members.length
                          ? `${members.length} symbols`
                          : `${visibleMembers.length} of ${members.length} symbols`}
                      </span>
                    </div>
                  )}

                  {group &&
                    expanded &&
                    visibleMembers.length === 0 && (
                      <div className="overrides-empty is-filter">
                        No symbols match “{groupSymbolSearch[groupId]}”.
                      </div>
                    )}

                  {group &&
                    expanded &&
                    visibleMembers.map((member) => (
                      <div
                        className={`overrides-row is-child ${
                          member.hasOwnOverride ? "is-own-override" : ""
                        }`}
                        key={member.symbolName}
                      >
                        <div>
                          <span className="overrides-child-name">
                            {member.symbolName}
                          </span>
                          {member.hasOwnOverride ? (
                            <>
                              <span className="overrides-badge is-symbol">
                                Own override
                              </span>
                              <span className="overrides-badge is-group">
                                Overrides {row.groupName || member.groupName}
                              </span>
                            </>
                          ) : (
                            <span className="overrides-child-meta">
                              uses this group rate
                            </span>
                          )}
                        </div>
                        {(page?.hierarchy || []).map((column) => {
                          const source = member.hasOwnOverride
                            ? overrides.find(
                                (item) =>
                                  isSymbolScope(item.scope) &&
                                  (item.symbolName || "").toLowerCase() ===
                                    member.symbolName.toLowerCase(),
                              )
                            : row;
                          const share = (source?.allocations || []).find((item) =>
                            sameId(item.ibUserId, column.ibUserId),
                          );
                          return (
                            <span
                              className="overrides-static"
                              key={column.ibUserId}
                            >
                              {money(share?.commissionPerLot)}
                            </span>
                          );
                        })}
                        <span className="overrides-total">
                          {money(
                            rowTotal(
                              member.hasOwnOverride
                                ? overrides.find(
                                    (item) =>
                                      isSymbolScope(item.scope) &&
                                      (item.symbolName || "").toLowerCase() ===
                                        member.symbolName.toLowerCase(),
                                  ) || row
                                : row,
                            ),
                          )}
                        </span>
                        <span>
                          {canUpdate && !member.hasOwnOverride && (
                            <button
                              type="button"
                              className="overrides-own"
                              onClick={() => addSymbolOverride(member.symbolName)}
                            >
                              Override symbol
                            </button>
                          )}
                        </span>
                      </div>
                    ))}
                </React.Fragment>
              );
            })}
          </div>
        </Spin>

        <div className="overrides-actions">
          <button
            type="button"
            className="inline-flex h-9 items-center rounded-full border border-gray-200 bg-white px-5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            disabled={!isDirty || loading}
            onClick={load}
          >
            Discard
          </button>
          {canUpdate && (
            <LoadableButton
              type="button"
              className="h-9 bg-primary text-sm text-white font-medium rounded-full px-5 themeHover"
              lable="Save"
              isLoading={saving}
              disabled={saving || loading || !isDirty || hasRowErrors}
              loadingLable="Saving..."
              onClick={handleSave}
            />
          )}
        </div>
      </div>
    </div>
  );
}
